import { useRouter } from 'next/router';
import Head from 'next/head';
import { useQuery, useApolloClient } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import _map from 'lodash/map';
import _get from 'lodash/get';
import Main from '../../../../components/main';
import Loading from '../../../../components/loading';
import ErrorMessage from '../../../../components/error-message';
import NotFound from '../../../../components/not-found';
import EventCalendar from '../../../../components/dynamic-event-calendar';
import { groupCalendarGridQuery, groupCalendarEventsQuery } from '../../../../apollo/groups';
import { buildTitle } from '../../../../utils/layout';
import config from '../../../../config';
import CalendarHeader from '../../../../components/groups/calendar-header';
import { buildImageUrl } from '../../../../utils/images';
import GroupHeader from '../../../../components/groups/header';
import GroupCalendarFooter from '../../../../components/groups/calendar-footer';

const useStyles = makeStyles((theme) => ({
  addButton: {
    margin: theme.spacing(2, 0),
    whiteSpace: 'nowrap',
  },
  calendar: {
    margin: theme.spacing(2, 0),
  },
  buttons: {
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
}));

const buildJsonLdOrganization = (group) => {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": group.name,
    "url": `${config().universal.baseUri}/groups/${group.slug}`
  };
  if (group.image) {
    ld.logo = buildImageUrl(group.image, 'json-ld');
  }
  return ld;
};

const buildJsonLDBreadcrumbs = (group) => {
  const items = [
    {
      '@id': `${config().universal.baseUri}/cities/${group.city.slug}`,
      name: group.city.name,
    },
    {
      '@id': `${config().universal.baseUri}/groups/${group.slug}`,
      name: group.name,
    },
    {
      '@id': `${config().universal.baseUri}/groups/${group.slug}/calendar/grid`,
      name: 'Calendar Grid',
    },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: _map(items, (item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item,
    })),
  };
};

const GroupCalendarGridPage = () => {
  const classes = useStyles();
  const router = useRouter();
  const { query } = router;
  const client = useApolloClient();

  const { data, loading, error } = useQuery(groupCalendarGridQuery, {
    variables: {
      slug: query.slug,
    },
  });
  const group = _get(data, 'group');

  function handleLoadEvents({ start, end }) {
    return client.query({
      query: groupCalendarEventsQuery,
      variables: {
        slug: group.slug,
        start,
        end,
      },
    }).then(({ data }) => data.group.calendarEvents);
  }

  if (error) {
    return <ErrorMessage message='An error occurred while loading group.' error={error} />;
  }
  if (loading) { return <Loading />; }
  if (!group || group.found) { return <NotFound />; }

  const canonicalUrl = `${config().universal.baseUri}/groups/${group.slug}/calendar/grid`;
  const description = `${group.name}'s community calendar of group rides and bike events.`;

  return (
    <Main>
      <Head>
        <title>{buildTitle('Calendar Grid', group.name)}</title>
        {!group.discoverable && <meta name="robots" content="noindex" />}
        <link rel="canonical" href={canonicalUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLdOrganization(group))
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildJsonLDBreadcrumbs(group))
          }}
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`${group.name} Calendar`} />
        <meta
          property="og:description"
          content={description}
        />
        <meta name="description" content={description} />
      </Head>

      <GroupHeader group={group} tab="calendar" />
      <CalendarHeader title="Calendar" tab="grid" group={group} />

      <div className={classes.calendar}>
        <EventCalendar timeZone={group.city.timezone} loadEvents={handleLoadEvents} />
      </div>

      <GroupCalendarFooter group={group} />
    </Main>
  );
};

export default GroupCalendarGridPage;
